import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MenuBarItemEnum } from '../../../../shared/enums/menu-bar-items.enum';
import { MenuItem } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { IToken } from '../../../../shared/interfaces/token.interface';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SeverityEnum } from '../../../../shared/enums/severity.enum';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApplicationSettingsDialogComponent } from '../../../dialogs/application-settings-dialog/application-settings-dialog.component';
import { ApplicationSettingsService } from '../../../../shared/services/applicationsettings.service';
import { map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {
  IGetApplicationSettings,
  IUpdateApplicationSettingsRequest,
} from '../../../../shared/interfaces/application-settings.interface';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorService } from '../../../../shared/services/error.service';
import { CountryService } from '../../../../shared/services/country.service';
import { INamedEntity } from '../../../../shared/interfaces/namedEntity.interface';
import { Roles } from '../../../../shared/enums/roles.enum';
import { AccessService } from '../../../../shared/services/access.service';
import { VendorService } from '../../../../modules/admin/services/vendor.service';
import { IVendorCountryData, IVendorCountryResult } from '../../../../modules/admin/interfaces/vendor.interface';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { ActionEnum } from '../../../../shared/enums/action.enum';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private authenticationService = inject(AuthenticationService);
  private notificationService = inject(NotificationService);
  private applicationSettingsService = inject(ApplicationSettingsService);
  private dialogService = inject(DialogService);
  private errorService = inject(ErrorService);
  private countryService = inject(CountryService);
  private destroy$: Subject<void> = new Subject<void>();
  private readonly accessService = inject(AccessService);
  private readonly vendorService = inject(VendorService);

  items: MenuItem[] = [];
  selectedItem: unknown;
  loggedUser!: string;
  countries!: INamedEntity[];
  selectedCountryId!: number;
  selectedVendorGuid!: string | null;
  isVendorSelectActive: boolean = false;
  isVendorClearActive: boolean = false;
  vendors$!: Observable<IVendorCountryData[]>;

  countries$ = this.countryService.getCountries();

  ngOnInit(): void {
    this.items = this.generateMenuItems();
    const tokenData: IToken = this.authenticationService.extractToken();
    this.loggedUser = tokenData.fullname;
    this.selectedCountryId = +tokenData.countryId;

    const role = tokenData.role;

    switch (role) {
      case Roles.SuperAdmin:
      case Roles.NLBLGSales:
      case Roles.CountryAdmin:
        if (this.selectedCountryId) {
          this.isVendorSelectActive = true;
          this.isVendorClearActive = true;
          this.vendors$ = this.vendorService.getHeaderVendorsByCountry(this.selectedCountryId).pipe(
            map((response: HttpResponse<IVendorCountryResult>) => {
              if (response.body?.result && response.body.result.length > 0) {
                this.selectedVendorGuid = response.body.result[0].guid;
                this.vendorService.changedActiveVendor(this.selectedVendorGuid);
              } else {
                this.selectedVendorGuid = null;
                this.vendorService.changedActiveVendor(null);
              }
              return response.body?.result || [];
            })
          );
        }
        break;
      case Roles.VendorAdmin:
        this.isVendorSelectActive = true;
        this.isVendorClearActive = false;
        this.vendors$ = this.vendorService.getVendorsByUser().pipe(
          map((response: HttpResponse<IVendorCountryResult>) => {
            if (response.body?.result && response.body.result.length > 0) {
              this.selectedVendorGuid = response.body.result[0].guid;
              this.vendorService.changedActiveVendor(this.selectedVendorGuid);
            } else {
              this.selectedVendorGuid = null;
              this.vendorService.changedActiveVendor(null);
            }

            return response.body?.result || [];
          })
        )
        break;
      case Roles.User:
        this.isVendorSelectActive = true;
        this.isVendorClearActive = false;
        this.vendors$ = this.vendorService.getVendorsByUser().pipe(
          map((response: HttpResponse<IVendorCountryResult>) => {
            if (response.body?.result && response.body.result.length > 0) {
              this.selectedVendorGuid = response.body.result[0].guid;
              this.vendorService.changedActiveVendor(this.selectedVendorGuid);
            } else {
              this.selectedVendorGuid = null;
              this.vendorService.changedActiveVendor(null);
            }

            return response.body?.result || [];
          })
        );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Generate menu items
   *
   * @private
   * @return {*}  {MenuItem[]}
   * @memberof HeaderComponent
   */
  private generateMenuItems(): MenuItem[] {
    return [
      {
        label: this.translocoService.translate('core.menu.items.vehicles'),
        id: MenuBarItemEnum.Vehicles,
        routerLink: [`/${MenuBarItemEnum.Vehicles}`],
        hasAccess: this.accessService.hasAccess([
          Roles.SuperAdmin,
          Roles.CountryAdmin,
          Roles.VendorAdmin,
          Roles.User,
          Roles.NLBUser,
          Roles.NLBLGSales
        ])
      },
      {
        label: this.translocoService.translate('core.menu.items.offers'),
        id: MenuBarItemEnum.Offers,
        routerLink: [`/${MenuBarItemEnum.Offers}`],
        hasAccess: this.accessService.hasAccess([
          Roles.SuperAdmin,
          Roles.CountryAdmin,
          Roles.VendorAdmin,
          Roles.User,
          Roles.NLBUser,
          Roles.NLBLGSales
        ])
      },
      {
        label: this.translocoService.translate('core.menu.items.admin'),
        id: MenuBarItemEnum.Admin,
        routerLink: [`/${MenuBarItemEnum.Admin}`],
        hasAccess: this.accessService.hasAccess([
          Roles.SuperAdmin,
          Roles.CountryAdmin,
          Roles.NLBLGSales
        ])
      },
    ];
  }

  /**
   * Method navigates to homepage
   *
   * @memberof HeaderComponent
   */
  goHome(): void {
    this.router.navigateByUrl('/');
  }

  /**
   * The `logout` function logout the user from application by calling the `logout` method of the
   * `authenticationService`, and then redirects the user to the login page if the logout is successful,
   * or displays an error message if it fails.
   *
   * @memberof HeaderComponent
   */
  logout(): void {
    this.authenticationService.logout().then((valid: boolean) => {
      if (valid) {
        this.router.navigateByUrl('/login');
      } else {
        const title = this.translocoService.translate('errors.default.title');
        const message = this.translocoService.translate(
          'errors.default.message',
        );
        this.notificationService.showToast(title, message, SeverityEnum.Error);
      }
    });
  }

  editApplicationSettings(): void {
    this.applicationSettingsService
      .getApplicationSettings(this.selectedVendorGuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: HttpResponse<IGetApplicationSettings>) => {
        if (data.status === HttpStatusCode.Ok) {
          this.applicationSettingsDialog(data.body);
        } else {
          this.errorService.responseErrorHandler(data.status);
        }
      });
  }

  checkUserRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.User) {
      return true;
    }
    return false;
  }

  checkSuperAdminRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.SuperAdmin) {
      return true;
    }
    return false;
  }

  checkCountryAdminRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.CountryAdmin) {
      return true;
    }
    return false;
  }

  checkVendorAdminRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.VendorAdmin) {
      return true;
    }

    return false;
  }

  checkNLBLGSalesRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.NLBLGSales) {
      return true;
    }
    return false;
  }

  applicationSettingsDialog(model?: IGetApplicationSettings | null): void {
    const ref: DynamicDialogRef = this.dialogService.open(
      ApplicationSettingsDialogComponent,
      {
        modal: true,
        data: {
          model: model,
        },
        closable: true,
        width: '50%',
        height: '100vh',
        contentStyle: { overflow: 'hidden' },
        baseZIndex: 10000,
        maximizable: false,
        styleClass: 'application-settings-dialog-top',
        header: this.translocoService.translate(
          'applicationSettings.labels.edit',
        ),
      },
    );

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IUpdateApplicationSettingsRequest | boolean) => {
        if (data) {
          if (typeof data !== 'boolean') {
            this.applicationSettingsService
              .updateApplicationSettings(data)
              .pipe(takeUntil(this.destroy$))
              .subscribe((data: HttpResponse<void>) => {
                if (data.status !== HttpStatusCode.Ok) {
                  this.errorService.responseErrorHandler(data.status);
                  this.notificationService.showSuccessToast(ActionEnum.Updated);
                }
              });
          }
        }
      });
  }

  changeCountry(event: any): void {
    const refreshToken = this.authenticationService.getRefreshToken();
    const subscription: Subscription = this.countryService
      .changeCountry(event.value, refreshToken!)
      .subscribe((data: HttpResponse<any>) => {
        subscription.unsubscribe();
        this.selectedVendorGuid = null;
        this.vendorService.changedActiveVendor(this.selectedVendorGuid);
        if (data.status === HttpStatusCode.Ok) {
          sessionStorage.setItem('token', data.body?.result as string);
          window.location.reload();
        }
      });
  }

  changeVendor(event: DropdownChangeEvent): void {
    const vendorGuid = event.value;
    this.vendorService.changedActiveVendor(vendorGuid);
  }

  isActive(item: any): boolean {
    return this.router.url === item.routerLink[0];
  }
}
